import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { apiFetchBlob } from '@shared/http/client';
import { useUserStore } from '@shared/stores/user';

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
    });
}

function sanitizeFilename(filename: string): string {
    return filename.replace(/([^a-z0-9 .]+)/gi, '-');
}

function randomizeFilename(filename: string): string {
    return `${Math.random().toString(36).substring(2, 8)}_${filename}`;
}

export async function openPrivacyPdf(): Promise<void> {
    const { isLoggedIn } = useUserStore();
    // Angular PrivacyService: a configured `privacyUrl` wins over both API endpoints — and it does so
    // regardless of isCustomizedApp, so this branch is also reachable from the button variant, not
    // only from the tab that links the URL directly.
    const privacyUrl = window.kgrDynamicConfig.privacyUrl ?? null;
    if (privacyUrl !== null) {
        const blob = await apiFetchBlob(privacyUrl);
        await openFile(blob, 'datenschutz.pdf', 'application/pdf');
        return;
    }
    let blob: Blob;
    try {
        blob = await apiFetchBlob(isLoggedIn ? '/privacy-information' : '/static-privacy-information');
    } catch {
        blob = await apiFetchBlob('/static-privacy-information');
    }
    await openFile(blob, 'datenschutz.pdf', 'application/pdf');
}

// Native: persist the blob to Documents and hand it to the OS file opener. Shared by both
// openFile() and downloadBlob() — on native there is no browser download, both paths save + open.
async function saveAndOpenNative(blob: Blob, filename: string, mimeType: string): Promise<void> {
    const safeFilename = randomizeFilename(sanitizeFilename(filename));
    const base64 = await blobToBase64(blob);
    const writeResult = await Filesystem.writeFile({
        path: safeFilename,
        data: base64,
        directory: Directory.Documents,
    });
    await FileOpener.open({ filePath: writeResult.uri, contentType: mimeType });
}

// "View" semantics: web opens the file in a new tab (e.g. a privacy PDF the user reads inline).
export async function openFile(blob: Blob, filename: string, mimeType: string): Promise<void> {
    if (window.kgrCapacitor) {
        await saveAndOpenNative(blob, filename, mimeType);
    } else {
        window.open(URL.createObjectURL(blob), '_blank');
    }
}

// "Download" semantics: web saves the file via an <a download> click (a Capacitor WebView can't,
// so native saves to Documents + opens it instead). mimeType defaults to the blob's own type.
export async function downloadBlob(blob: Blob, filename: string, mimeType?: string): Promise<void> {
    const type = mimeType || blob.type || 'application/octet-stream';
    if (window.kgrCapacitor) {
        await saveAndOpenNative(blob, filename, type);
    } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }
}
